/** An imported project on its design page (docs/features/migration-opensolar.md): "Imported from OpenSolar" with the
 * source's stage and system summary, the import note, its files (read-only), and "Design with AI" to build the design
 * (1.00 Lux, the switch bonus's design Lux first; asks first like every Lux spend). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Import, Sparkles } from "lucide-react";
import { useState, type MouseEvent } from "react";
import { ApiError, api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { useOrg } from "../api/org";
import { useLuxWallet } from "../api/queries";
import { activityHref, ratePrice, spendableLux } from "../lib/luxSpend";
import { formatBytes } from "../lib/migration";
import { date, money, num } from "../lib/format";
import { useLeadActivity } from "./LeadActivity";
import { useLuxSpend } from "./LuxSpend";
import { Alert, Badge, Button, Card, useToast } from "./ui";
import { useT } from "../i18n/I18nProvider";
import { PIPELINE_STATUS_LABELS } from "../pages/Designs";

function parts(list: S["ImportPart"][] | undefined): string {
  return (list ?? []).map((p) => [p.quantity ? `${num(p.quantity)} ×` : null, p.manufacturer, p.model].filter(Boolean).join(" ")).join(", ");
}

export function ImportedSummary({ orgId, project, lead }: { orgId: string; project: S["Project"]; lead: S["Lead"] | null | undefined }) {
  const t = useT();
  const s = project.import_summary;
  const mapped = lead?.pipeline_status ? PIPELINE_STATUS_LABELS[lead.pipeline_status] : undefined;
  const activity = useLeadActivity(orgId, lead?.public_quote_id);
  const note = activity.data?.notes?.[0];
  const files = useQuery({
    queryKey: ["project-attachments", project.id],
    queryFn: () => unwrap(api.GET("/v1/projects/{project_id}/attachments", { params: { path: { project_id: project.id } } })),
  });
  const toast = useToast();
  const download = useMutation({
    mutationFn: async (file: S["ProjectAttachment"]) => {
      const response = await rawFetch(`/api/v1/projects/${project.id}/attachments/${file.id}`);
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <Card
      title={
        <span className="row" style={{ gap: 8 }}>
          <Import size={17} /> {t("designs.origin.import")}
        </span>
      }
      description={t("designs.imported.description")}
    >
      <div className="stack" style={{ gap: 12 }}>
        {(lead?.stage_label || s?.stage) && (
          <div className="row" style={{ gap: 8 }}>
            <span className="badge plain info">{lead?.stage_label ?? s?.stage}</span>
            {lead?.pipeline_status && <span className="small muted">{t("designs.imported.here", { status: mapped ? t(mapped) : lead.pipeline_status })}</span>}
            {s?.milestone && <span className="small muted">· {t("designs.imported.milestone", { milestone: s.milestone })}</span>}
          </div>
        )}
        {s && (s.kw || s.modules?.length) ? (
          <dl className="kv" data-testid="import-summary">
            {s.kw != null && (
              <>
                <dt>{t("designs.column.system")}</dt>
                <dd>
                  {num(s.kw, 2)} kW{s.module_count ? ` · ${t("designs.imported.modulesCount", { count: s.module_count })}` : ""}
                </dd>
              </>
            )}
            {!!s.modules?.length && (
              <>
                <dt>{t("designs.imported.modules")}</dt>
                <dd className="ellipsis">{parts(s.modules)}</dd>
              </>
            )}
            {!!s.inverters?.length && (
              <>
                <dt>{t("designs.imported.inverters")}</dt>
                <dd className="ellipsis">{parts(s.inverters)}</dd>
              </>
            )}
            {!!s.battery_kwh && (
              <>
                <dt>{t("designs.imported.battery")}</dt>
                <dd>
                  {num(s.battery_kwh, 1)} kWh{s.batteries?.length ? ` · ${parts(s.batteries)}` : ""}
                </dd>
              </>
            )}
            {s.annual_kwh != null && (
              <>
                <dt>{t("designs.metric.production")}</dt>
                <dd>
                  {num(s.annual_kwh)} {t("designs.metric.kwhPerYear")}
                </dd>
              </>
            )}
            {s.price != null && s.price > 0 && (
              <>
                <dt>{t("designs.metric.price")}</dt>
                <dd>{t("designs.imported.inclTax", { amount: money(s.price, "USD") })}</dd>
              </>
            )}
            {(s.systems ?? 0) > 1 && (
              <>
                <dt>{t("designs.imported.systems")}</dt>
                <dd>{t("designs.imported.systemsCount", { count: s.systems ?? 0 })}</dd>
              </>
            )}
          </dl>
        ) : (
          <p className="muted small" style={{ margin: 0 }}>
            {t("designs.imported.noSystem")}
          </p>
        )}
        {note && (
          <p className="small muted" style={{ margin: 0 }} data-testid="import-note">
            <b>{note.title}</b> · {date(note.at, true)}
            {note.detail ? ` · ${note.detail}` : ""}
          </p>
        )}
        {!!files.data?.items.length && (
          <div>
            <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
              {t("designs.imported.files", { count: files.data.items.length })}
            </div>
            <ul className="attachment-list">
              {files.data.items.map((f) => (
                <li key={f.id}>
                  <FileText size={14} />
                  <button className="linklike ellipsis" onClick={() => download.mutate(f)} title={t("designs.imported.downloadFile", { name: f.filename })}>
                    {f.filename}
                  </button>
                  <span className="muted small">{formatBytes(f.size_bytes)}</span>
                  {f.tags.slice(0, 2).map((t) => (
                    <Badge key={t} plain>
                      {t}
                    </Badge>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

/** "Design with AI" for an imported project's empty design. */
export function ImportedDesignWithAi({ orgId, designId }: { orgId: string; designId: string }) {
  const t = useT();
  const { can } = useOrg();
  const wallet = useLuxWallet(orgId);
  const lux = useLuxSpend();
  const qc = useQueryClient();
  const toast = useToast();
  const [started, setStarted] = useState(false);
  const cost = ratePrice(wallet.data?.rate_card, "design", 100);
  const ai = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/designs/{design_id}/imported/ai-design", { params: { path: { design_id: designId } } })),
  });
  const start = async (event: MouseEvent<HTMLButtonElement>) => {
    const anchor = event.currentTarget;
    const req = { cost, label: t("designs.imported.designWithAi"), balance: spendableLux(wallet.data, true), worthMinor: wallet.data?.unit_minor != null ? Math.floor((cost * wallet.data.unit_minor) / 100) : null };
    if (!(await lux.confirm(req, anchor))) return;
    ai.mutate(undefined, {
      onSuccess: (made) => {
        setStarted(true);
        lux.spent({ amount: cost, label: t("designs.imported.designWithAi"), left: made.lux_left ?? null, href: activityHref("design") });
        void qc.invalidateQueries({ queryKey: ["design", designId] });
        void qc.invalidateQueries({ queryKey: ["leads-infinite"] });
        void qc.invalidateQueries({ queryKey: ["lux", orgId] });
      },
      onError: (e) => {
        if (e instanceof ApiError && e.status === 402) void lux.confirm({ ...req, canPay: false }, anchor);
        else toast(errorMessage(e), true);
      },
    });
  };
  if (started)
    return (
      <Alert tone="info" title={t("designs.imported.designing")}>
        {t("designs.imported.designingBody")}
      </Alert>
    );
  return (
    <div className="stack" style={{ alignItems: "center", textAlign: "center", gap: 10 }}>
      <Import size={28} className="faint" />
      <b>{t("designs.imported.empty")}</b>
      <p className="muted small" style={{ margin: 0, maxWidth: 420 }}>
        {t("designs.imported.emptyBody")}
      </p>
      {can("designs_leads", "create") && (
        <Button variant="primary" icon={<Sparkles size={16} />} loading={ai.isPending} onClick={(e) => void start(e)}>
          {t("designs.imported.designWithAi")}
        </Button>
      )}
    </div>
  );
}
